/** Approximate V8 overhead per object property slot (bytes). */
export const OBJECT_SLOT_BYTES = 32;

/** Approximate size of a IEEE-754 number in heap (bytes). */
export const NUMBER_BYTES = 8;

/** Approximate boolean boxed size (bytes). */
export const BOOLEAN_BYTES = 4;

/** Base overhead attributed to each Map/Set entry (bytes). */
export const COLLECTION_ENTRY_OVERHEAD_BYTES = 24;

/**
 * Estimates UTF-16 heap footprint of a string value.
 *
 * @param value String payload.
 * @returns Approximate byte size.
 */
export function estimateStringBytes( value: string ): number {
    return OBJECT_SLOT_BYTES + value.length * 2;
}

/**
 * Estimates serialized JSON size in UTF-8 bytes (proxy for retained object graphs).
 *
 * @param value Any JSON-serializable value.
 * @returns Byte estimate, or `0` when serialization fails.
 */
export function estimateJsonUtf8Bytes( value: unknown ): number {
    try {
        const json = JSON.stringify( value );

        return json.length;
    } catch {
        return 0;
    }
}

/**
 * Formats a byte count for compact console output.
 *
 * @param bytes Raw byte estimate.
 * @returns Human-readable size (B, KB, or MB).
 */
export function formatBytes( bytes: number ): string {
    if ( bytes < 1024 ) {
        return `${ bytes } B`;
    }
    if ( bytes < 1024 * 1024 ) {
        return `${ ( bytes / 1024 ).toFixed( 1 ) } KB`;
    }

    return `${ ( bytes / ( 1024 * 1024 ) ).toFixed( 2 ) } MB`;
}

/**
 * Reads Chromium `performance.memory` when available.
 *
 * @returns JS heap usage metrics, or empty object on unsupported browsers.
 */
export function readJsHeapMetrics(): {
    "usedJSHeapSize"?:  number;
    "totalJSHeapSize"?: number;
} {
    const memoryInfo = typeof performance !== "undefined"
        ? ( performance as Performance & {
            "memory"?: {
                "usedJSHeapSize":  number;
                "totalJSHeapSize": number;
            };
        } ).memory
        : undefined;

    if ( memoryInfo === undefined ) {
        return {};
    }

    return {
        "usedJSHeapSize":  memoryInfo.usedJSHeapSize,
        "totalJSHeapSize": memoryInfo.totalJSHeapSize,
    };
}
